import type { FleetShip } from "../state";
import { state } from "../state";
import { triggerSave } from "../persistence";
import type { ScreenContext } from "../app";
import { renderHeader } from "../components";
import { effectiveYieldRate } from "../../miningYield";
import { startingFuelCapacity } from "../../fuel";
import { startingProvisionsCapacity } from "../../provisions";
import { effectiveNow } from "../../gameClock";
import { hourlyUpkeepCr } from "../../shipUpkeep";
import { getSystem } from "../../data/loadSystems";
import { findBaseAt } from "../../baseStorage";
import {
  describeDock,
  dismissHiredOnHullChange,
  dockKey,
  STATION_LOCATION_ID
} from "../../shipStorage";
import { shipIconHtml } from "../shipIcon";
import { formatEndurance, fuelHeadline, fuelSummary, provisionsSummary } from "../../endurance";

// Fleet roster -- design doc Section 10's "Escort Autonomy & Fleet
// Management UI" (LOCKED fields, UI chrome wireframed). Purely for
// managing an already-assembled fleet (state.fleet, see state.ts) --
// hiring/buying happen at the Shipyard and Captain's Lounge instead.
//
// Real fields shown per ship: name, Class/Size (role/hullTier), variant,
// owned-vs-hired tag, a real (stored) Stance toggle, max EHP, max cargo
// capacity, and DPS/Mining rate when the ship's real role implies that
// capability (effectiveYieldRate for Digger/Packmule -- the same "who can
// mine" convention missionBoardScreen.ts and droneSystem.ts's
// shipHasDefaultDroneBay already use elsewhere, since PlayerShip has no
// per-hull fitted-module list to check instead).
//
// Active-mode escort AI is real now (16.43): every "Active" fleet ship
// other than whichever one is being flown joins the Expedition View as a
// real ship with its own hull/shield/dps/cargo capacity, auto-targeting
// the nearest hostile within its own detection range and passively
// regenerating shield/hull from its own real stats -- Stance genuinely
// changes what happens in space now, not just a stored-but-inert toggle.
// "Auto-pulling cargo from another fleet ship" specifically isn't
// modeled -- a fleet ship's cargo only fills via Loot explicitly
// targeting it (expeditionScreen.ts), nothing pulls automatically.
//
// **Upkeep is shown for real now (16.76).** The old note here said it
// couldn't be, for want of a game clock and a Provisions/Fuel mechanic --
// all three shipped since (16.34 clock, 16.50 Fuel, 16.62 Provisions,
// 16.54 hired-ship credit upkeep), so the roster now shows each hired
// ship's real hourly charge and the fleet's combined burn. Owned ships
// deliberately show no credit figure: Section 10 keeps owned-ship upkeep
// to food/fuel only, which is consumed as Provisions/Fuel rather than
// billed.
//
// What the doc specifies but still isn't modeled here, and why:
// - **Live shield/hull bars and a live cargo ratio** only make sense for
//   a ship actually present in a combat/mining scene -- this roster
//   screen itself has no live combat state to read for a ship that isn't
//   currently in the Expedition View, so it shows max capacity instead,
//   explicitly labeled as such, not a live number.
// - **Narrative flavor** (ship/crew flavor text) is skipped -- there's no
//   crew-personality data anywhere to draw from, and inventing flavor
//   text with zero backing felt worse than just omitting the
//   (collapsed-by-default, non-functional) section.
//
// Ship HIRING is not here either -- it moved to the Shipyard in 16.76,
// so buying and hiring a hull sit side by side in one place.

export function renderFleetScreen(container: HTMLElement, ctx: ScreenContext): void {
  renderHeader(container, ctx);

  const wrap = document.createElement("div");
  container.appendChild(wrap);

  function render(): void {
    // Hired ships bill credits per real-clock-hour (16.54); owned ships
    // don't (Section 10: food/fuel only, consumed rather than billed).
    const hourlyBurn = state.fleet
      .filter((f) => f.source === "hired")
      .reduce((sum, f) => sum + hourlyUpkeepCr(f.ship), 0);

    wrap.innerHTML = `
      <h1>Fleet Roster</h1>
      <p class="dim">
        ${state.fleet.length} ship${state.fleet.length === 1 ? "" : "s"} in your fleet.
        ${
          hourlyBurn > 0
            ? `Hired-ship upkeep: <strong>${Math.round(hourlyBurn).toLocaleString()} CR/hour</strong> combined, billed automatically while the game clock runs.`
            : "No hired ships, so no credit upkeep. Owned ships cost food and fuel only (Section 10)."
        }
      </p>
      ${
        fleetIsReadOnly()
          ? `<p class="dim fleet-readonly-note">In flight &mdash; the roster is <strong>read-only</strong>. Dock at a Station or Base to change stances or dismiss hired ships.</p>`
          : ""
      }
      <div class="ship-list">
        ${
          state.fleet.length === 0
            ? '<p class="dim">No ships yet. Buy or hire one at the Shipyard.</p>'
            : state.fleet.map((f) => fleetRowHtml(f)).join("")
        }
      </div>

    `;

    wrap.querySelectorAll<HTMLButtonElement>("button[data-stance]").forEach((btn) => {
      btn.addEventListener("click", () => {
        toggleStance(btn.dataset.stance!);
      });
    });
    wrap.querySelectorAll<HTMLButtonElement>("button[data-fly]").forEach((btn) => {
      btn.addEventListener("click", () => {
        flyShip(btn.dataset.fly!);
      });
    });
    wrap.querySelectorAll<HTMLButtonElement>("button[data-view]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const f = state.fleet.find((x) => x.id === btn.dataset.view);
        if (!f) return;
        state.shipCardTarget = { ship: f.ship, instanceId: f.id };
        ctx.navigate("shipCard");
      });
    });
    wrap.querySelectorAll<HTMLButtonElement>("button[data-dismiss]").forEach((btn) => {
      btn.addEventListener("click", () => {
        dismissShip(btn.dataset.dismiss!);
      });
    });

    wrap.querySelector<HTMLButtonElement>("[data-leave]")?.addEventListener("click", () => {
      leaveShipHere();
    });
  }

  function toggleStance(id: string): void {
    // Guarded here as well as on the button (16.99): a disabled attribute is a
    // hint, not a rule, and this one writes to the save.
    if (fleetIsReadOnly()) return;
    const f = state.fleet.find((x) => x.id === id);
    if (!f) return;
    f.stance = f.stance === "Active" ? "Passive" : "Active";
    triggerSave(ctx);
    render();
  }

  function flyShip(id: string): void {
    const f = state.fleet.find((x) => x.id === id);
    if (!f) return;
    // Only OWNED hulls can be flown (16.72). A hired ship is commanded by
    // its own captain and crew -- the player directs it, never pilots it.
    if (f.source === "hired") {
      state.lastEvent = `${f.ship.name} is hired -- it comes with its own captain. You can only fly ships you own.`;
      render();
      return;
    }
    // Changing hull ends every hired contract outright (16.72) -- a hired
    // captain has no reason to sit idle on an employer who just walked
    // away, and idle escorts silently billing upkeep is exactly the
    // invisible drain the design avoids elsewhere.
    const dismissed = releaseHiredEscorts();
    // The hull being left behind stays where it is; the one being boarded
    // is now with the captain, so it is no longer in storage.
    const outgoing = state.fleet.find((x) => x.id === state.currentShipInstanceId);
    if (outgoing && !outgoing.dockedAtKey) {
      outgoing.dockedAtKey = dockKey(state.currentSystemId, state.currentLocationId);
    }
    f.dockedAtKey = undefined;
    state.currentShip = f.ship;
    state.currentShipInstanceId = f.id; // reuse this fleet entry's own stable id -- carries over any Armory upgrades already on this specific ship
    state.fuelCapacity = startingFuelCapacity(f.ship.hullTierLabel);
    state.fuelOnBoard = state.fuelCapacity;
    state.provisionsCapacity = startingProvisionsCapacity(f.ship.crew);
    state.provisionsOnBoard = state.provisionsCapacity;
    state.provisionsLastUpdatedAt = effectiveNow(state.clock, Date.now());
    state.isLoanerShip = false;
    state.lastEvent =
      `Now flying ${f.ship.name} (${f.ship.variant}).` +
      (dismissed > 0
        ? ` ${dismissed} hired escort${dismissed === 1 ? "" : "s"} released — their contracts ended when you changed hulls.`
        : "");
    triggerSave(ctx);
    ctx.rerender();
  }

  // Shared by every hull change (16.72). Returns how many contracts ended
  // so the caller can say so out loud -- a roster that silently shrinks
  // is worse than one that explains itself.
  function releaseHiredEscorts(): number {
    const result = dismissHiredOnHullChange(state.fleet);
    state.fleet = result.fleet;
    return result.dismissed;
  }

  // Leave the current hull parked here and step into the Escape Pod
  // (16.72). Only valid at a real dock -- a hull abandoned in open space
  // would be unreachable forever. Pod travel is safe by design, so this
  // is a deliberate logistics move, not a last resort.
  function leaveShipHere(): void {
    const current = state.fleet.find((f) => f.id === state.currentShipInstanceId);
    if (!current || !atDockNow()) return;
    const dismissed = releaseHiredEscorts();
    current.dockedAtKey = dockKey(state.currentSystemId, state.currentLocationId);
    state.currentShip = null;
    state.currentShipInstanceId = null;
    state.isLoanerShip = false;
    state.inEscapePod = true;
    state.fuelOnBoard = 0;
    state.fuelCapacity = 0;
    state.provisionsOnBoard = 0;
    state.provisionsCapacity = 0;
    state.lastEvent =
      `Left ${current.ship.name} docked here — you're in your pod.` +
      (dismissed > 0 ? ` ${dismissed} hired escort${dismissed === 1 ? "" : "s"} released.` : "");
    triggerSave(ctx);
    ctx.navigate("escapePod");
  }

  function dismissShip(id: string): void {
    if (fleetIsReadOnly()) return;
    const f = state.fleet.find((x) => x.id === id);
    if (!f || f.source !== "hired") return;
    state.fleet = state.fleet.filter((x) => x.id !== id);
    // A ship that's gone takes its damage record with it (16.81) --
    // otherwise a re-hire that happened to reuse the id would inherit the
    // last ship's battle damage.
    delete state.shipDamage[id];
    state.lastEvent = `Dismissed ${f.ship.name} (${f.ship.variant}).`;
    triggerSave(ctx);
    render();
  }

  render();
}

// Captain Skills deliberately do NOT live here -- they belong to the
// player-captain rather than to any hull or roster, and live on the
// Captain's Lounge alongside the rest of their career record (16.76).

// Both of these read live `state` and are used from fleetRowHtml (which
// is module-level, outside the component closure), so they live out here
// too rather than being threaded through as parameters.
function atDockNow(): boolean {
  if (state.currentLocationId === STATION_LOCATION_ID) {
    return getSystem(state.currentSystemId)?.hasStation ?? false;
  }
  return !!findBaseAt(state.bases, state.currentSystemId, state.currentLocationId);
}

// A stored hull can only be boarded from the exact dock it's parked at.
function boardableHere(f: FleetShip): boolean {
  return (
    f.source === "owned" &&
    atDockNow() &&
    f.dockedAtKey === dockKey(state.currentSystemId, state.currentLocationId)
  );
}

/**
 * Read-only in flight (16.99, Don's ruling).
 *
 * The roster is worth READING mid-flight -- which escort is where, what it
 * costs, how much hull it has -- so the screen stays reachable from the
 * Expedition View. Changing it is a different matter: reassigning stances and
 * dismissing hired crews are dock-side decisions, and doing them from open
 * space made the roster a remote control for ships light-years away.
 *
 * Board and Leave were already gated on being physically at the right dock
 * (boardableHere / atDockNow), so the two that actually needed locking are
 * Stance and Dismiss.
 */
function fleetIsReadOnly(): boolean {
  return state.lastTopLevelView === "expedition";
}

/**
 * Fuel and Provisions endurance for one roster entry (16.103, Don).
 *
 * The rates have existed since 16.50 and 16.62; nothing in the game ever showed
 * them. Don asked for this in the Fleet Roster "for sure", and it is the right
 * home: the roster is the one screen listing every hull side by side, which is
 * exactly where a per-hull consumption rate becomes comparable rather than
 * abstract.
 *
 * Two honest limits are stated rather than glossed over:
 *
 * 1. Only the FLOWN ship has real Fuel and Provisions pools. A stored or escort
 *    hull has consumption RATES but no live tank, so it shows what it *would*
 *    burn, not what it has. Presenting a shared pool as if each ship carried its
 *    own would be a fiction the save does not back.
 * 2. Provisions burn the flown ship's crew only (`provisions.ts`), not the whole
 *    fleet's. The Crew Quarters lot shows a FLEET crew total, which reads as if
 *    it drove the burn -- so both are labelled explicitly.
 */
function enduranceLineHtml(f: FleetShip, isFlying: boolean): string {
  const s = f.ship;
  const prov = provisionsSummary(s.crew, state.provisionsOnBoard, state.provisionsCapacity);
  const fuel = fuelSummary(s.hullTierLabel, state.fuelOnBoard, state.fuelCapacity);
  if (!isFlying) {
    // Rates only -- this hull is not the one burning anything right now.
    return `Would burn <strong>${fuel.fastTravelCost}</strong> fuel/jump &middot; <strong>${fuel.warpCost}</strong> fuel/warp &middot; <strong>${prov.perHour.toFixed(1)}</strong> provisions/hour for ${s.crew} crew`;
  }
  const lowClass = prov.low ? ' class="fleet-endurance-low"' : "";
  return `
    <div>Fuel <strong>${Math.round(fuel.onBoard)}</strong> / ${Math.round(fuel.capacity)} &mdash; ${fuelHeadline(fuel)}
      <span class="dim">(${fuel.fastTravelCost}/jump, ${fuel.warpCost}/warp, Move To free)</span></div>
    <div${lowClass}>Provisions <strong>${prov.onBoard.toFixed(1)}</strong> / ${prov.capacity.toFixed(1)} &mdash; ${formatEndurance(prov.hoursLeft)} left
      <span class="dim">(${prov.perHour.toFixed(1)}/hour for ${prov.crew} crew)</span></div>
  `;
}

function fleetRowHtml(f: FleetShip): string {
  const readOnly = fleetIsReadOnly();
  const lockedTitle = "Dock at a Station or Base to change the roster";
  const canLeave = atDockNow();
  const s = f.ship;
  const ehp = s.hullHp + s.shieldHp;
  // Compared by fleet-entry INSTANCE id, not name/variant: the roster can
  // legitimately hold several identical hulls (an owned Digger I and a
  // hired one), and matching on name marked every copy as "flying" the
  // moment one of them was. Instance id is the only real identity here.
  const isFlying = state.currentShipInstanceId === f.id;
  const canMine = s.role === "Digger" || s.role === "Packmule";
  const capabilityStats = [
    s.dps > 0 ? `${s.dps} DPS` : null,
    canMine ? `${effectiveYieldRate(s.hullTier).toFixed(2)} m3/s mining` : null
  ]
    .filter(Boolean)
    .join(" &middot; ");

  return `
    <div class="ship-card">
      <div class="ship-card-title" style="display:flex; align-items:center; gap:0.55rem;">
        ${shipIconHtml(s, "md")}
        <strong>${s.name}</strong> <span class="pill">${s.variant}</span> <span class="pill">${s.role}</span>
        <span class="pill ${f.source === "owned" ? "tag-solo" : "tag-flee"}">${f.source}</span>
        ${isFlying ? '<span class="pill">flying</span>' : ""}
      </div>
      <div class="ship-card-stats dim">
        Max EHP ${ehp} &middot; Max Cargo ${s.cargoM3} m3 · Ore ${s.oreHoldM3} m3${capabilityStats ? ` &middot; ${capabilityStats}` : ""}
      </div>
      <div class="ship-card-stats dim">
        ${enduranceLineHtml(f, isFlying)}
      </div>
      <div class="ship-card-stats dim">
        ${
          f.source === "hired"
            ? `Upkeep ${Math.round(hourlyUpkeepCr(s)).toLocaleString()} CR/hour`
            : "Upkeep: food &amp; fuel only (no credit charge)"
        }
        ${
          f.source === "owned" && !isFlying
            ? ` &middot; Stored at ${describeDock(f.dockedAtKey, (id) => getSystem(id)?.name)}`
            : ""
        }
      </div>
      <div class="cargo-actions">
        <button type="button" data-stance="${f.id}" class="pill-btn" ${readOnly ? `disabled title="${lockedTitle}"` : ""}>Stance: ${f.stance}</button>
        <div style="display:flex; gap:0.5rem;">
          <button type="button" class="btn-secondary" data-view="${f.id}">View</button>
          ${
            isFlying
              ? canLeave
                ? `<button type="button" class="btn-secondary" data-leave>Leave here &amp; take pod</button>`
                : ""
              : f.source === "hired"
                ? ""
                : `<button type="button" data-fly="${f.id}" ${boardableHere(f) ? "" : "disabled"} title="${boardableHere(f) ? "Board this ship" : "You must be at the dock where this ship is stored"}">Board</button>`
          }
          ${f.source === "hired" ? `<button type="button" class="btn-secondary" data-dismiss="${f.id}" ${readOnly ? `disabled title="${lockedTitle}"` : ""}>Dismiss</button>` : ""}
        </div>
      </div>
    </div>
  `;
}
